/**
 * Interpreta a mensagem de erro do processo "Download de Xmls Completos".
 *
 * O backend grava uma string corrida, cortada em 3999 caracteres:
 * `<N> de <M> XMLs não puderam ser gerados: <doc1>: <motivo1> | <doc2>: <motivo2> | ...`
 *
 * `<doc>` é o identificador da venda ou `compra <id da nota>` para nota de compra.
 * Arquivo sem Vue e sem alias `@/` para rodar no jest.
 */

export type TipoDeDocumentoDoXml = 'Venda' | 'Nota de compra'

export interface XmlNaoGerado {
	tipo: TipoDeDocumentoDoXml
	identificador: string
	motivo: string
	/** O texto do motivo chegou cortado pelo limite de tamanho da mensagem. */
	motivoCortado: boolean
}

export interface ResumoDeXmlsNaoGerados {
	/** N da mensagem; null quando o cabeçalho não foi reconhecido. */
	quantidadeComFalha: number | null
	/** M da mensagem; null quando o cabeçalho não foi reconhecido. */
	quantidadeTotal: number | null
	documentos: XmlNaoGerado[]
	/** A mensagem tinha menos documentos do que N, ou terminava num trecho sem motivo. */
	listaCortada: boolean
	/** Preenchido quando a mensagem não está no formato esperado: mostrar o texto como veio. */
	textoBruto: string | null
}

const CABECALHO = /^\s*(\d+)\s+de\s+(\d+)\s+XMLs?\s+n[ãa]o\s+puderam\s+ser\s+gerados?\s*:\s*/i
const SEPARADOR_DE_DOCUMENTOS = ' | '
const SEPARADOR_DE_MOTIVO = ': '
const PREFIXO_DE_COMPRA = /^compra\s+/i
/** O backend corta a mensagem neste tamanho. */
export const LIMITE_DA_MENSAGEM = 3999

export function resumoVazio(): ResumoDeXmlsNaoGerados {
	return {
		quantidadeComFalha: null,
		quantidadeTotal: null,
		documentos: [],
		listaCortada: false,
		textoBruto: null,
	}
}

function escaparParaRegex(texto: string): string {
	return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function semIdentificadorRepetido(motivo: string, identificador: string): string {
	const redundante = new RegExp(`\\s+para o identificador\\s+${escaparParaRegex(identificador)}(?=\\s|$)`, 'i')
	return motivo.replace(redundante, '').trim()
}

function lerDocumento(trecho: string): XmlNaoGerado | null {
	const posicao = trecho.indexOf(SEPARADOR_DE_MOTIVO)
	if (posicao < 0) return null

	const documento = trecho.substring(0, posicao).trim()
	const motivoOriginal = trecho.substring(posicao + SEPARADOR_DE_MOTIVO.length).trim()
	if (!documento || !motivoOriginal) return null

	const ehCompra = PREFIXO_DE_COMPRA.test(documento)
	const identificador = ehCompra ? documento.replace(PREFIXO_DE_COMPRA, '').trim() : documento
	if (!identificador) return null

	return {
		tipo: ehCompra ? 'Nota de compra' : 'Venda',
		identificador,
		motivo: semIdentificadorRepetido(motivoOriginal, identificador) || motivoOriginal,
		motivoCortado: false,
	}
}

export function interpretarXmlsNaoGerados(mensagem: string | null | undefined): ResumoDeXmlsNaoGerados {
	const texto = (mensagem || '').trim()
	if (!texto) return resumoVazio()

	const cabecalho = CABECALHO.exec(texto)
	if (!cabecalho) return { ...resumoVazio(), textoBruto: texto }

	const quantidadeComFalha = Number(cabecalho[1])
	const quantidadeTotal = Number(cabecalho[2])
	const resto = texto.substring(cabecalho[0].length)

	const trechos = resto
		.split(SEPARADOR_DE_DOCUMENTOS)
		.map(trecho => trecho.trim())
		.filter(trecho => !!trecho)

	const documentos: XmlNaoGerado[] = []
	let descartouTrecho = false
	trechos.forEach(trecho => {
		const documento = lerDocumento(trecho)
		if (documento) documentos.push(documento)
		else descartouTrecho = true
	})

	if (!documentos.length && trechos.length) {
		return { ...resumoVazio(), quantidadeComFalha, quantidadeTotal, textoBruto: texto }
	}

	const listaCortada = descartouTrecho || documentos.length < quantidadeComFalha

	const chegouNoLimite = (mensagem || '').length >= LIMITE_DA_MENSAGEM
	const ultimoTrechoFoiLido = !!lerDocumento(trechos[trechos.length - 1] || '')
	if (listaCortada && chegouNoLimite && ultimoTrechoFoiLido) {
		documentos[documentos.length - 1].motivoCortado = true
	}

	return {
		quantidadeComFalha,
		quantidadeTotal,
		documentos,
		listaCortada,
		textoBruto: null,
	}
}
